import React, { useMemo } from "react";
import { InvoiceSentStatus } from "@common/types/invoice";
import {
    getInvoiceSenderName,
    INVOICE_SENT_STATUS_LABELS,
} from "@common/helpers/domains/invoices/invoiceSentInfo";
import { noop } from "@common/helpers/function";
import {
    FilterMultiselect,
    FilterMultiselectOption,
    FilterMultiselectValue,
} from "@components/pageHeader/filters/FilterMultiselect/FilterMultiselect";
import { useCurrentWorkspaceMembers } from "@api/workspaceMember.api";
import { buildWorkspaceMemberFilterOptions } from "@components/pageHeader/filters/workspaceMemberFilter.lib";
import { BaseFilterProps } from "../../lib";
import styles from "./InvoiceSentStatusFilter.module.scss";

const SECTION_STATUS = "Status";
const SECTION_SENT_BY = "Sent by";

const isInvoiceSentStatus = (value: FilterMultiselectValue) =>
    Object.values<FilterMultiselectValue>(InvoiceSentStatus).includes(value);

const statusOptions: FilterMultiselectOption[] = Object.values(
    InvoiceSentStatus,
).map((sentStatus) => ({
    value: sentStatus,
    label: INVOICE_SENT_STATUS_LABELS[sentStatus],
    searchableLabel: INVOICE_SENT_STATUS_LABELS[sentStatus],
    section: SECTION_STATUS,
}));

export const InvoiceSentStatusFilter: React.FC<BaseFilterProps> = ({
    filters,
    onChange,
}) => {
    const { data: members = [] } = useCurrentWorkspaceMembers();
    const selectedSenderIds = filters.sentByUserIds;

    const options = useMemo(
        (): FilterMultiselectOption[] => [
            ...statusOptions,
            ...buildWorkspaceMemberFilterOptions({
                members,
                selectedUserIds: selectedSenderIds,
                getName: getInvoiceSenderName,
                section: SECTION_SENT_BY,
            }),
        ],
        [members, selectedSenderIds],
    );

    const selectedValues = [
        ...(filters.sentStatuses ?? []),
        ...(selectedSenderIds ?? []),
    ];

    return (
        <div className={styles.filter}>
            <FilterMultiselect
                maxListHeight={340}
                inDropdown={false}
                searchable
                options={options}
                value={selectedValues}
                onChange={(nextValues) => {
                    const sentStatuses = nextValues.filter(
                        (selectedValue): selectedValue is InvoiceSentStatus =>
                            typeof selectedValue === "string" &&
                            isInvoiceSentStatus(selectedValue),
                    );
                    const sentByUserIds = nextValues.filter(
                        (selectedValue): selectedValue is number =>
                            typeof selectedValue === "number",
                    );
                    onChange({
                        sentStatuses:
                            sentStatuses.length > 0 ? sentStatuses : undefined,
                        sentByUserIds:
                            sentByUserIds.length > 0
                                ? sentByUserIds
                                : undefined,
                    });
                }}
                name="invoiceSent"
                onBlur={noop}
            />
        </div>
    );
};
